import { useState, useCallback, useRef } from 'react';
import { ExpandedState } from '@tanstack/react-table';
import { FieldFilter } from '../../../../../../utils/fieldMappingSearch';

export interface UseFieldMappingsStateReturn {
  // Dialog state
  isDialogOpen: boolean;
  editingDestinationPath: string | undefined;
  showRequiredHint: boolean;
  openDialog: (path: string) => void;
  closeDialog: () => void;
  setShowRequiredHint: (show: boolean) => void;

  // Filter state
  searchQuery: string;
  fieldFilter: FieldFilter;
  setSearchQuery: (query: string) => void;
  setFieldFilter: (filter: FieldFilter) => void;

  // Expansion state
  expanded: ExpandedState;
  userExpanded: ExpandedState;
  setExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setUserExpanded: React.Dispatch<React.SetStateAction<ExpandedState>>;
  handleExpandedChange: (updater: any) => void;

  // Initialization tracking
  initialExpansionSet: React.MutableRefObject<boolean>;
}

/**
 * Custom hook for managing TreeFieldMappingsTable state
 * Handles dialog, search/filter, and tree expansion state
 */
export const useFieldMappingsState = (): UseFieldMappingsStateReturn => {
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDestinationPath, setEditingDestinationPath] = useState<
    string | undefined
  >();
  const [showRequiredHint, setShowRequiredHint] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [fieldFilter, setFieldFilter] = useState<FieldFilter>('all');

  // Expanded state for tree rows
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Track the user's base expansion state (without auto-expand from filtering)
  const [userExpanded, setUserExpanded] = useState<ExpandedState>({});

  // Track if initial expansion has been set (only expand on first load)
  const initialExpansionSet = useRef(false);

  // Dialog handlers
  const openDialog = useCallback((path: string) => {
    setEditingDestinationPath(path);
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingDestinationPath(undefined);
  }, []);

  // Handle user manually expanding/collapsing nodes
  const handleExpandedChange = useCallback((updater: any) => {
    setExpanded((prev) => {
      const newExpanded =
        typeof updater === 'function' ? updater(prev) : updater;
      // Update the user's base expansion state
      setUserExpanded(newExpanded);
      return newExpanded;
    });
  }, []);

  return {
    // Dialog state
    isDialogOpen,
    editingDestinationPath,
    showRequiredHint,
    openDialog,
    closeDialog,
    setShowRequiredHint,

    // Filter state
    searchQuery,
    fieldFilter,
    setSearchQuery,
    setFieldFilter,

    // Expansion state
    expanded,
    userExpanded,
    setExpanded,
    setUserExpanded,
    handleExpandedChange,

    // Initialization tracking
    initialExpansionSet,
  };
};
